// src/pages/modules/DebtTracking/DebtTracking.tsx
// ติดตามหนี้สิน — Head ดูทุกสาย / Follower ดูสายตัวเองเท่านั้น
// + จัดการพนักงานติดตาม (assign โซน)

import React from 'react'
import {
  Alert, Badge, Button, Card, Col, Form,
  Modal, Row, Spinner, Table,
} from 'react-bootstrap'
import { AuthContext, dbRead, dbSet, dbUpdate, saveLog } from '../../../_services/firebase'
import { getAllCustomers, PATH } from '../../../_services/customer.service'
import { formatCurrency, formatDate, toastDanger, toastSuccess } from '../../../_utils'
import type { CustomerReg, LoanData, LoanDataFollow, InstallmentItem, AdminRecord } from '../../../_types'
import { DB_ADMIN, DB_FOLLOWER, DB_FOLLOWER_BY_ID, DB_FOLLOWING, DB_FOLLOWING_BY_ID, DB_COLLECT_BY_ID, DB_COLLECT_IMG_ITEM, DB_FOLLOWING_CUSTOM_ZONE, DB_FOLLOWING_CUSTOM_ZONE_BY_ID } from '../../../_config/db_paths'
import PhotoGallery from '../../../components/PhotoGallery/PhotoGallery'

// ─── Types ────────────────────────────────────────────────────
interface FollowerDoc {
  uid: string
  zones: string[]
  updatedAt: number
}

// ─── Following (ช่องทางติดต่อเพิ่มเติม ต่อลูกค้า) ──────────────
interface FollowingContact {
  type: string       // phone | line | facebook | whatsapp | telegram | other
  label?: string     // ชื่อช่องทาง (ใช้เมื่อ type === 'other')
  value: string
}
interface FollowingDoc {
  [x: string]: any
  contacts: FollowingContact[]
  note: string
  updatedAt: number
  updatedBy: string
}

const CONTACT_TYPES: { key: string; label: string; color: string }[] = [
  { key: 'phone', label: 'เบอร์โทร', color: '#2e7d32' },
  { key: 'line', label: 'LINE', color: '#06c755' },
  { key: 'facebook', label: 'Facebook', color: '#1877f2' },
  { key: 'whatsapp', label: 'WhatsApp', color: '#25d366' },
  { key: 'telegram', label: 'Telegram', color: '#229ed9' },
  { key: 'other', label: 'อื่นๆ', color: '#777' },
]

// แปลง contacts ที่อ่านจาก Firebase (array หรือ object) → array
function toContactArray(raw: any): FollowingContact[] {
  if (Array.isArray(raw)) return raw.filter(Boolean)
  if (raw && typeof raw === 'object') return Object.values(raw) as FollowingContact[]
  return []
}

// สร้างลิงก์เปิด/ติดต่อจากค่า contact (ถ้าทำได้)
function contactHref(c: FollowingContact): string | null {
  const v = (c.value || '').trim()
  if (!v) return null
  if (/^https?:\/\//i.test(v)) return v
  if (c.type === 'phone') return `tel:${v}`
  if (c.type === 'whatsapp') { const d = v.replace(/[^\d]/g, ''); return d ? `https://wa.me/${d}` : null }
  return null
}

// ─── Helpers ──────────────────────────────────────────────────
function paidCount(ins: InstallmentItem[]): number {
  return ins.filter((i) => i.status === 'paid').length
}
function calcRemain(loan: LoanData, ins: InstallmentItem[]): number {
  const paid = ins.reduce((s, i) => s + (i.paidAmount || 0), 0)
  return Math.max(0, loan.totalWithInterest - paid)
}
function todayStatus(ins: InstallmentItem[]): 'paid' | 'overdue' | 'pending' {
  const unpaid = ins.filter((i) => i.status === 'pending' || i.status === 'partial')
  if (unpaid.length === 0) return 'paid'
  const next = unpaid[0]
  if (!next.paidAt && paidCount(ins) < ins.length) return 'overdue'
  return 'pending'
}

// ─── CustomerTrackCard ────────────────────────────────────────
function CustomerTrackCard({
  reg, loan, installments, contactCount = 0, onDetail, onContact, onReceipt, onZone, onEdit, role,
}: {
  reg: CustomerReg
  loan: LoanDataFollow | null
  installments: InstallmentItem[]
  contactCount?: number
  onDetail: () => void
  onContact: () => void
  onReceipt: () => void
  onZone?: () => void
  onEdit?: () => void
  role: string
}) {
  const paid = paidCount(installments)
  const total = installments.length
  const status = todayStatus(installments)
  const remain = loan ? calcRemain(loan as LoanData, installments) : 0

  return (
    <Card className='shadow-sm' style={{ fontSize: 13 }}>
      <Card.Body>
        <div className='d-flex justify-content-between align-items-start mb-2'>
          <div>
            <div style={{ fontWeight: 700 }}>{reg.personal.fullName}</div>
            <div style={{ fontSize: 11, color: '#888' }}>{formatDate(reg.meta.updatedAt)}</div>
          </div>
          <Badge
            bg={status === 'paid' ? 'success' : status === 'overdue' ? 'danger' : 'secondary'}
            style={{ fontSize: 10 }}
          >
            {status === 'paid' ? 'จ่ายแล้ว' : status === 'overdue' ? 'รอชำระ' : 'รอชำระ'}
          </Badge>
        </div>

        {/* Phone + Location */}
        {reg.personal.phone && (
          <div className='mb-1' style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ flex: 1, background: '#f5f5f5', borderRadius: 6, padding: '4px 8px', fontSize: 12 }}>
              เบอร์โทรศัพท์
              <div style={{ fontWeight: 700, color: '#1565c0' }}>{reg.personal.phone}</div>
            </div>
            <Button
              size='sm' variant='primary'
              style={{ fontSize: 11, padding: '4px 10px' }}
              onClick={() => window.open(`tel:${reg.personal.phone}`)}
            >โทร</Button>
          </div>
        )}
        {reg.personal.locationLink && (
          <div className='mb-2' style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ flex: 1, background: '#f5f5f5', borderRadius: 6, padding: '4px 8px', fontSize: 11, wordBreak: 'break-all' }}>
              โลเคชั่น
              <div style={{ color: '#555' }}>{reg.personal.locationLink}</div>
            </div>
            <Button
              size='sm' variant='warning'
              style={{ fontSize: 11, padding: '4px 10px' }}
              onClick={() => window.open(reg.personal.locationLink, '_blank')}
            >ไป</Button>
          </div>
        )}

        {loan && (
          <div style={{ marginBottom: 6 }}>
            <div className='d-flex align-items-center gap-2'>
              <span>โซน: <strong>{loan.zone || '-'}</strong></span>
              {(role === 'super admin' || role === 'debhead') && (onZone && (
                <Button size='sm' variant='outline-secondary' style={{ fontSize: 10, padding: '0 6px' }}
                  title='ปรับโซน' onClick={onZone}>
                  <i className='fa-solid fa-pen' />
                </Button>
              ))}
            </div>
            <div style={{ fontSize: 11, color: '#888' }}>โซน: <strong>{loan.zoneOld || '-'} (ค่า ใน excel)</strong></div>
            <div style={{ color: '#c62828', fontWeight: 700 }}>คงเหลือ: {formatCurrency(remain)}</div>
            <div>ค้างชำระ: <strong style={{ color: '#f44336' }}>{total - paid}</strong></div>
            <div>จ่ายแล้ว: {paid}/{total}</div>
            <div>สถานะวันนี้:{' '}
              <Badge bg={status === 'paid' ? 'success' : 'danger'} style={{ fontSize: 10 }}>
                {status === 'paid' ? 'จ่ายแล้ว' : 'ยังไม่ได้จ่าย'}
              </Badge>
            </div>
          </div>
        )}

        <div className='d-flex gap-2 mt-2'>
          <Button variant='outline-primary' size='sm' className='flex-fill' onClick={onDetail}>ดูรายละเอียด</Button>
          <Button variant='outline-success' size='sm' className='flex-fill' onClick={onContact}>
            <i className='fa-solid fa-address-book me-1' />ติดต่อ{contactCount ? ` (${contactCount})` : ''}
          </Button>
          <Button variant='success' size='sm' className='w-100 mt-2' onClick={onReceipt}>
            <i className='fa-solid fa-camera me-1' />รับเงิน / ถ่ายรูป
          </Button>
        </div>
      </Card.Body>
    </Card>
  )
}

// ─── Detail Modal ─────────────────────────────────────────────
function DetailModal({
  show, reg, loan, installments, onClose,
}: {
  show: boolean
  reg: CustomerReg | null
  loan: LoanData | null
  installments: InstallmentItem[]
  onClose: () => void
}) {
  if (!reg) return null
  const remain = loan ? calcRemain(loan, installments) : 0
  const paid = paidCount(installments)

  // วันที่ของแต่ละงวด: ใช้ dueAt ถ้ามี ไม่งั้นคำนวณจากวันเริ่มสัญญา (จาก key) + (งวด−1) วัน
  const DAY_MS = 86400000
  const startMs = Number(String(reg.key).split('_')[1]) || 0
  const fmtDMY = (ms: number) =>
    new Date(ms).toLocaleDateString('en-GB', {
      day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Asia/Bangkok',
    })
  const dueDateOf = (ins: InstallmentItem) => {
    const at = (ins as any).dueAt
    if (at) return fmtDMY(at)
    if (startMs) return fmtDMY(startMs + (ins.seq - 1) * DAY_MS)
    return `งวด ${ins.seq}`
  }

  return (
    <Modal show={show} onHide={onClose} size='lg' centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 15 }}>รายละเอียดลูกค้า — {reg.personal.fullName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className='g-3'>
          <Col xs={12} md={6}>
            <Card className='shadow-sm'>
              <Card.Header style={{ fontSize: 12, fontWeight: 700 }}>ข้อมูลส่วนตัว</Card.Header>
              <Card.Body style={{ fontSize: 12 }}>
                <Table size='sm' style={{ marginBottom: 0 }}>
                  <tbody>
                    {[
                      ['ชื่อ-นามสกุล', reg.personal.fullName],
                      ['เลขบัตร', reg.personal.nationalId || '-'],
                      ['วันเกิด', reg.personal.birthDate || '-'],
                      ['อายุ', reg.personal.age || '-'],
                      ['โทรศัพท์', reg.personal.phone || '-'],
                      ['ที่อยู่', reg.personal.address || '-'],
                      ['สถานะภาพ', reg.personal.maritalStatus || '-'],
                      ['Facebook', reg.personal.facebookLink || '-'],
                    ].map(([k, v]) => (
                      <tr key={k}><td style={{ fontWeight: 600, color: '#555' }}>{k}</td><td>{v}</td></tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} md={6}>
            {loan && (
              <Card className='shadow-sm'>
                <Card.Header style={{ fontSize: 12, fontWeight: 700 }}>ข้อมูลหนี้สิน</Card.Header>
                <Card.Body style={{ fontSize: 12 }}>
                  <Table size='sm' style={{ marginBottom: 0 }}>
                    <tbody>
                      {[
                        ['ยอดกู้', `${formatCurrency(loan.actualAmount)} กีบ`],
                        ['ดอกเบี้ย', `${loan.interestRate}%`],
                        ['ยอดรวม', `${formatCurrency(loan.totalWithInterest)} กีบ`],
                        ['ส่ง/วัน', `${formatCurrency(loan.dailyPayment)} กีบ`],
                        ['คงเหลือ', `${formatCurrency(remain)} กีบ`],
                        ['ชำระแล้ว', `${paid}/${installments.length} งวด`],
                        ['โซน', loan.zone || '-'],
                      ].map(([k, v]) => (
                        <tr key={k}><td style={{ fontWeight: 600, color: '#555' }}>{k}</td><td>{v}</td></tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}
          </Col>

          {/* Installment table */}
          <Col xs={12}>
            <PhotoGallery customerId={reg.key} photoCount={reg.meta.photoCount} />
          </Col>
          <Col xs={12}>
            <Card className='shadow-sm'>
              <Card.Header style={{ fontSize: 12, fontWeight: 700 }}>ตารางงวด</Card.Header>
              <Card.Body style={{ padding: 0, maxHeight: 260, overflowY: 'auto' }}>
                <Table size='sm' bordered style={{ fontSize: 11, marginBottom: 0 }}>
                  <thead className='table-light sticky-top'>
                    <tr>
                      <th>งวด</th><th>วันที่</th><th>ยอด</th><th>จ่ายจริง</th><th>สถานะ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {installments.map((ins) => (
                      <tr key={ins.seq}>
                        <td>
                          {dueDateOf(ins)}
                          <div style={{ fontSize: 9, color: '#aaa' }}>งวด {ins.seq}</div>
                        </td>
                        <td>{ins.paidAt ? (typeof ins.paidAt === 'number' ? formatDate(ins.paidAt) : ins.paidAt) : '-'}</td>
                        <td>{formatCurrency(ins.dueAmount)}</td>
                        <td>{ins.paidAmount > 0 ? formatCurrency(ins.paidAmount) : '-'}</td>
                        <td>
                          <Badge bg={
                            ins.status === 'paid' ? 'success' :
                              ins.status === 'partial' ? 'warning' : 'danger'
                          } style={{ fontSize: 10 }}>
                            {ins.status === 'paid' ? 'จ่ายแล้ว' : ins.status === 'partial' ? 'บางส่วน' : 'รอ'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='outline-secondary' size='sm' onClick={onClose}>ปิด</Button>
      </Modal.Footer>
    </Modal>
  )
}

// ─── ContactModal (ช่องทางติดต่อ following) ───────────────────
function ContactModal({
  show, reg, uid, following, onClose, onSaved,
}: {
  show: boolean
  reg: CustomerReg | null
  uid: string
  following: FollowingDoc | null
  onClose: () => void
  onSaved: (id: string, doc: FollowingDoc) => void
}) {
  const [contacts, setContacts] = React.useState<FollowingContact[]>([])
  const [note, setNote] = React.useState('')
  const [savingF, setSavingF] = React.useState(false)

  React.useEffect(() => {
    if (!show) return
    setContacts(toContactArray(following?.contacts))
    setNote(following?.note || '')
  }, [show, reg?.key, following])

  const addContact = () => setContacts((p) => [...p, { type: 'phone', value: '' }])
  const removeContact = (i: number) => setContacts((p) => p.filter((_, idx) => idx !== i))
  const updateContact = (i: number, patch: Partial<FollowingContact>) =>
    setContacts((p) => p.map((c, idx) => (idx === i ? { ...c, ...patch } : c)))

  const saveFollowing = async () => {
    if (!reg) return
    setSavingF(true)
    try {
      const cleaned: FollowingContact[] = contacts
        .map((c) => {
          const e: FollowingContact = { type: c.type, value: (c.value || '').trim() }
          if (c.type === 'other' && (c.label || '').trim()) e.label = c.label!.trim()
          return e
        })
        .filter((c) => c.value)
      const doc: FollowingDoc = { contacts: cleaned, note: note.trim(), updatedAt: Date.now(), updatedBy: uid }
      await dbUpdate(DB_FOLLOWING_BY_ID(reg.key), doc)
      await saveLog(uid, 'update', reg.key, `บันทึกช่องทางติดต่อ ${cleaned.length} ช่อง${doc.note ? ' + หมายเหตุ' : ''}`)
      onSaved(reg.key, doc)
      toastSuccess('บันทึกข้อมูลติดต่อเรียบร้อย')
      onClose()
    } catch (e: any) {
      toastDanger(e?.message || 'บันทึกไม่สำเร็จ')
    } finally {
      setSavingF(false)
    }
  }

  if (!reg) return null

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 15 }}>
          <i className='fa-solid fa-address-book me-2' />ช่องทางติดต่อ — {reg.personal.fullName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ fontSize: 12 }}>
        {reg.personal.phone && (
          <div className='mb-2 text-muted' style={{ fontSize: 11 }}>
            เบอร์จากใบสมัคร: <strong style={{ color: '#1565c0' }}>{reg.personal.phone}</strong>
          </div>
        )}
        {contacts.length === 0 && (
          <div className='text-muted mb-2'>ยังไม่มีช่องทางติดต่อ — กด “เพิ่มช่องทาง”</div>
        )}
        {contacts.map((c, i) => {
          const href = contactHref(c)
          return (
            <Row key={i} className='g-2 mb-2 align-items-center'>
              <Col xs={5} sm={3}>
                <Form.Select size='sm' value={c.type} onChange={(e) => updateContact(i, { type: e.target.value })}>
                  {CONTACT_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </Form.Select>
              </Col>
              {c.type === 'other' && (
                <Col xs={7} sm={3}>
                  <Form.Control size='sm' placeholder='ชื่อช่องทาง' value={c.label || ''} onChange={(e) => updateContact(i, { label: e.target.value })} />
                </Col>
              )}
              <Col xs={c.type === 'other' ? 12 : 7} sm>
                <Form.Control size='sm' placeholder='เบอร์ / ไอดี / ลิงก์' value={c.value} onChange={(e) => updateContact(i, { value: e.target.value })} />
              </Col>
              <Col xs='auto' className='d-flex gap-1'>
                {href && (
                  <Button size='sm' variant='outline-success' style={{ fontSize: 11 }} title='เปิด/ติดต่อ'
                    onClick={() => window.open(href, c.type === 'phone' ? '_self' : '_blank')}>
                    <i className='fa-solid fa-arrow-up-right-from-square' />
                  </Button>
                )}
                <Button size='sm' variant='outline-danger' style={{ fontSize: 11 }} title='ลบ' onClick={() => removeContact(i)}>
                  <i className='fa-solid fa-trash' />
                </Button>
              </Col>
            </Row>
          )
        })}
        <Button size='sm' variant='outline-primary' className='mt-1' style={{ fontSize: 11 }} onClick={addContact}>
          <i className='fa-solid fa-plus me-1' />เพิ่มช่องทาง
        </Button>
        <Form.Group className='mt-3'>
          <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>หมายเหตุ</Form.Label>
          <Form.Control as='textarea' rows={2} size='sm' placeholder='หมายเหตุเพิ่มเติม...' value={note} onChange={(e) => setNote(e.target.value)} />
        </Form.Group>
        <div className='text-muted mt-2' style={{ fontSize: 11 }}>
          {following?.updatedAt ? `แก้ไขล่าสุด ${formatDate(following.updatedAt)}` : 'ยังไม่เคยบันทึก'}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='outline-secondary' size='sm' onClick={onClose} disabled={savingF}>ปิด</Button>
        <Button variant='success' size='sm' disabled={savingF} onClick={saveFollowing}>
          {savingF ? <Spinner size='sm' animation='border' /> : <><i className='fa-solid fa-floppy-disk me-1' />บันทึก</>}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

// ─── CustomZoneModal (ปรับโซนรายลูกค้า → override) ─────────────
function CustomZoneModal({
  show, reg, loan, uid, allZones, onClose, onSaved,
}: {
  show: boolean
  reg: CustomerReg | null
  loan: LoanDataFollow | null
  uid: string
  allZones: string[]
  onClose: () => void
  onSaved: (id: string, zone: string) => void
}) {
  const [zone, setZone] = React.useState('')
  const [saving, setSaving] = React.useState(false)

  React.useEffect(() => { if (show) setZone(loan?.zone || '') }, [show, reg?.key])

  if (!reg) return null

  const save = async () => {
    setSaving(true)
    try {
      const z = zone.trim()
      await dbSet(DB_FOLLOWING_CUSTOM_ZONE_BY_ID(reg.key, ''), { zone: z, updatedAt: Date.now(), updatedBy: uid })
      await saveLog(uid, 'update', reg.key, `ปรับโซนติดตาม → ${z || '(ว่าง)'}`)
      onSaved(reg.key, z)
      toastSuccess('บันทึกโซนเรียบร้อย')
      onClose()
    } catch (e: any) {
      toastDanger(e?.message || 'บันทึกไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal show={show} onHide={onClose} centered size='sm'>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 15 }}>
          <i className='fa-solid fa-location-dot me-2' />ปรับโซน — {reg.personal.fullName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ fontSize: 12 }}>
        <div className='text-muted mb-2' style={{ fontSize: 11 }}>
          โซนจาก Excel (เดิม): <strong>{loan?.zoneOld || '-'}</strong>
        </div>
        <Form.Group>
          <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>โซนที่ใช้ติดตาม (override)</Form.Label>
          <Form.Control size='sm' value={zone} onChange={(e) => setZone(e.target.value)} placeholder='เช่น A หรือ B+C' />
          <div className='text-muted mt-1' style={{ fontSize: 11 }}>เว้นว่าง = ไม่มีโซน override</div>
        </Form.Group>
        {allZones.length > 0 && (
          <div className='d-flex flex-wrap gap-1 mt-2'>
            {allZones.map((z) => (
              <Button key={z} size='sm' variant='outline-secondary' style={{ fontSize: 11, padding: '1px 8px' }}
                onClick={() => setZone(z)}>{z}</Button>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='outline-secondary' size='sm' onClick={onClose} disabled={saving}>ยกเลิก</Button>
        <Button variant='warning' size='sm' onClick={save} disabled={saving}>
          {saving ? <Spinner size='sm' animation='border' /> : <><i className='fa-solid fa-floppy-disk me-1' />บันทึก</>}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

// ─── Zone Following Setting (ตั้งค่าโซน โดยผู้ติดตาม/ผู้จัดการ) ──────────────
function ZoneSettingModal({
  uid, role, allZones,
}: {
  uid: string
  role: string
  allZones: string[]
}) {
  const [show, setShow] = React.useState(false)
  const [zones, setZones] = React.useState<string[]>([])
  const [saving, setSaving] = React.useState(false)

  React.useEffect(() => {
    if (!show || !uid) return
    let mounted = true

    dbRead<FollowerDoc>(DB_FOLLOWER_BY_ID(uid))
      .then((doc) => {
        if (mounted) setZones(doc?.zones || [])
      })
      .catch(() => {
        if (mounted) setZones([])
      })

    return () => {
      mounted = false
    }
  }, [show, uid])

  const toggleZone = (zone: string) => {
    setZones((prev) => prev.includes(zone) ? prev.filter((z) => z !== zone) : [...prev, zone])
  }

  const saveZones = async () => {
    setSaving(true)
    try {
      const doc: FollowerDoc = { uid, zones, updatedAt: Date.now() }
      await dbSet(DB_FOLLOWER_BY_ID(uid), doc)
      await saveLog(uid, 'update', uid, `ตั้งค่าโซนติดตาม ${zones.join(', ') || 'ไม่มีโซน'}`)
      toastSuccess('บันทึกโซนเรียบร้อย')
      setShow(false)
    } catch (e: any) {
      toastDanger(e?.message || 'บันทึกไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Button size='sm' variant='outline-secondary' className='w-100' onClick={() => setShow(true)}>
        <i className='fa-solid fa-gear me-1' />โซน
      </Button>

      <Modal show={show} onHide={() => setShow(false)} centered size='sm'>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 15 }}>
            <i className='fa-solid fa-location-dot me-2' />ตั้งค่าโซนติดตาม
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: 12 }}>
          <div className='mb-2 text-muted' style={{ fontSize: 11 }}>
            {role === 'super admin' || role === 'debhead' || role === 'acchead'
              ? 'กำหนดโซนที่คุณต้องการติดตาม' : 'โซนที่คุณดูแล'}
          </div>

          {allZones.length === 0 ? (
            <div className='text-muted'>ยังไม่มีโซนในระบบ</div>
          ) : (
            <div className='d-flex flex-wrap gap-2'>
              {allZones.map((zone) => (
                <Form.Check
                  key={zone}
                  type='checkbox'
                  id={`zone_setting_${uid}_${zone}`}
                  label={zone}
                  checked={zones.includes(zone)}
                  onChange={() => toggleZone(zone)}
                  inline
                />
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='outline-secondary' size='sm' onClick={() => setShow(false)} disabled={saving}>
            ปิด
          </Button>
          <Button variant='warning' size='sm' onClick={saveZones} disabled={saving}>
            {saving ? <Spinner size='sm' animation='border' /> : <><i className='fa-solid fa-floppy-disk me-1' />บันทึก</>}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

// ─── Field Collection (รับเงินภาคสนาม รอลงบัญชี) ──────────────
interface Receipt {
  id: string
  amount: number
  hasImage?: boolean       // มีรูปแยกที่ collectImage/{cid}/{rid}
  note?: string
  collectedBy: string
  collectedAt: number
  status: 'pending'       // ยังไม่ลงบัญชี
}

// ย่อรูป → base64 jpeg
async function resizeImage(file: File, max = 1000, quality = 0.7): Promise<string> {
  const dataUrl = await new Promise<string>((res, rej) => {
    const r = new FileReader(); r.onload = () => res(r.result as string); r.onerror = () => rej(new Error('read')); r.readAsDataURL(file)
  })
  const img = await new Promise<HTMLImageElement>((res, rej) => {
    const im = new Image(); im.onload = () => res(im); im.onerror = () => rej(new Error('img')); im.src = dataUrl
  })
  let width = img.width, height = img.height
  if (width > max || height > max) {
    const s = Math.min(max / width, max / height); width = Math.round(width * s); height = Math.round(height * s)
  }
  const canvas = document.createElement('canvas'); canvas.width = width; canvas.height = height
  canvas.getContext('2d')!.drawImage(img, 0, 0, width, height)
  return canvas.toDataURL('image/jpeg', quality)
}

// โหลดรูป receipt (แยก node) เฉพาะตอนต้องใช้
async function loadCollectImage(cid: string, rid: string): Promise<string> {
  const doc = await dbRead<{ image?: string }>(DB_COLLECT_IMG_ITEM(cid, rid))
  return doc?.image || ''
}

function ReceiptModal({ show, reg, uid, onClose, onSaved, role }: {
  show: boolean; reg: CustomerReg | null; uid: string; onClose: () => void; onSaved?: (id: string) => void; role: string
}) {
  const [amount, setAmount] = React.useState('')
  const [payDate, setPayDate] = React.useState(new Date(Date.now() + 7 * 3600000).toISOString().slice(0, 16))
  const [note, setNote] = React.useState('')
  const [file, setFile] = React.useState<File | null>(null)
  const [preview, setPreview] = React.useState('')
  const [saving, setSaving] = React.useState(false)
  const [list, setList] = React.useState<Receipt[]>([])
  const [loadingList, setLoadingList] = React.useState(false)
  const [confirmDel, setConfirmDel] = React.useState<Receipt | null>(null)
  const [deleting, setDeleting] = React.useState(false)
  const [err, setErr] = React.useState('')
  const [imgCache, setImgCache] = React.useState<Record<string, string>>({})
  const [zoom, setZoom] = React.useState<string | null>(null)

  const nowLocalInput = () => new Date(Date.now() + 7 * 3600000).toISOString().slice(0, 16)

  React.useEffect(() => {
    if (!show || !reg) return
    setAmount(''); setNote(''); setFile(null); setPreview(''); setErr(''); setPayDate(nowLocalInput()); setImgCache({})
    setLoadingList(true)
    ;(async () => {
      try {
        const r = await dbRead<Record<string, Receipt>>(DB_COLLECT_BY_ID(reg.key))
        const arr = Object.values(r || {}).filter(Boolean).sort((a, b) => b.collectedAt - a.collectedAt)
        setList(arr)
        // โหลดรูปเฉพาะรายการที่มีรูป (แยก node)
        arr.filter((x) => x.hasImage).forEach(async (x) => {
          const img = await loadCollectImage(reg.key, x.id)
          if (img) setImgCache((p) => ({ ...p, [x.id]: img }))
        })
      } catch { setList([]) }
      finally { setLoadingList(false) }
    })()
  }, [show, reg?.key])

  const onPick = (f: File | null) => { setFile(f); setPreview(f ? URL.createObjectURL(f) : '') }

  const save = async () => {
    if (!reg) return
    setErr('')
    const amt = parseFloat(amount) || 0
    if (amt <= 0) { setErr('กรุณาระบุจำนวนเงินให้ถูกต้อง'); return }
    if (!file) { setErr('กรุณาแนบรูปสลิป/ภาพถ่ายเงินสดก่อนบันทึก'); return }
    if (file && !file.type.startsWith('image/')) { setErr('ไฟล์ต้องเป็นรูปภาพ'); return }

    setSaving(true)
    try {
      const id = `rcpt_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`
      let image: string | undefined
      if (file) image = await resizeImage(file)
      const collectedAt = payDate ? new Date(payDate + '+07:00').getTime() : Date.now()
      const rec: Receipt = {
        id, amount: amt, note: note.trim() || '',
        collectedBy: uid, collectedAt, status: 'pending',
        ...(image ? { hasImage: true } : {}),
      }
      await dbSet(`${DB_COLLECT_BY_ID(reg.key)}/${id}`, rec)
      if (image) await dbSet(DB_COLLECT_IMG_ITEM(reg.key, id), { image })   // รูปแยก node
      await saveLog(uid, 'update', reg.key, `รับเงินภาคสนาม ${formatCurrency(amt)}${image ? ' (มีรูป)' : ''}`)
      if (image) setImgCache((p) => ({ ...p, [id]: image! }))
      setList((p) => [rec, ...p])
      onSaved?.(reg.key)
      toastSuccess('บันทึกการรับเงินเรียบร้อย')
      setAmount(''); setNote(''); setPayDate(nowLocalInput()); setFile(null); setPreview('')
    } catch (e: any) {
      toastDanger(e?.message || 'บันทึกไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  if (!reg) return null
  const totalPending = list.reduce((s, r) => s + (r.amount || 0), 0)
  const doDelete = async () => {
    if (!reg || !confirmDel) return
    setDeleting(true)
    try {
      await dbDelete(`${DB_COLLECT_BY_ID(reg.key)}/${confirmDel.id}`)
      await dbDelete(DB_COLLECT_IMG_ITEM(reg.key, confirmDel.id))   // ลบรูปแยกด้วย
      setList((p) => p.filter((x) => x.id !== confirmDel.id))
      toastSuccess('ลบรายการเรียบร้อย')
      setConfirmDel(null)
    } catch (e: any) {
      toastDanger(e?.message || 'ลบไม่สำเร็จ')
    } finally {
      setDeleting(false)
    }
  }

  function dbDelete(path: string): Promise<void> {
    return dbSet(path, null)   // set null = ลบ node ใน RTDB
  }

  return (
    <Modal show={show} onHide={onClose} centered>

      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 15 }}>
          <i className='fa-solid fa-camera me-2' />รับเงิน — {reg.personal.fullName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ fontSize: 12 }}>
        <Alert variant='warning' style={{ fontSize: 11 }}>
          รายการนี้เป็นการบันทึกภาคสนาม (ยังไม่ลงบัญชี) รอฝ่ายบัญชีตรวจและยืนยัน
        </Alert>

        <Form.Group className='mb-2'>
          <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>จำนวนเงิน (กีบ) *</Form.Label>
          <Form.Control
            size='sm' type='text' inputMode='numeric' placeholder='0'
            value={amount ? Number(amount).toLocaleString('en-US') : ''}
            onChange={(e) => { setAmount(e.target.value.replace(/[^\d]/g, '')); setErr('') }}
          />
        </Form.Group>

        <Form.Group className='mb-2'>
          <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>รูปสลิป / ภาพถ่ายเงินสด *</Form.Label>
          <Form.Control size='sm' type='file' accept='image/*'
            onChange={(e) => onPick((e.target as HTMLInputElement).files?.[0] || null)} />
          {preview && (
            <div className='mt-2 text-center'>
              <img src={preview} alt='preview' style={{ maxWidth: '100%', maxHeight: 220, borderRadius: 6 }} />
            </div>
          )}
        </Form.Group>
        <Form.Group className='mb-2'>
          <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>วันที่รับเงิน</Form.Label>
          <Form.Control size='sm' type='datetime-local'
            value={payDate}
            onChange={(e) => setPayDate(e.target.value)}
          />
        </Form.Group>

        <Form.Group className='mb-2'>
          <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>หมายเหตุ</Form.Label>
          <Form.Control as='textarea' rows={2} size='sm' placeholder='เช่น รับที่บ้าน / จ่ายบางส่วน'
            value={note} onChange={(e) => setNote(e.target.value)} />
        </Form.Group>
        {err && <Alert variant='danger' className='py-1 px-2' style={{ fontSize: 12 }}>{err}</Alert>}
        <Button variant='success' size='sm' className='w-100' disabled={saving} onClick={save}>
          {saving ? <Spinner size='sm' animation='border' /> : <><i className='fa-solid fa-floppy-disk me-1' />บันทึกการรับเงิน</>}
        </Button>

        <hr />
        <div className='d-flex justify-content-between align-items-center mb-2'>
          <span style={{ fontWeight: 600 }}>รายการที่บันทึกไว้ (รอลงบัญชี)</span>
          {list.length > 0 && <Badge bg='warning' text='dark'>รวม {formatCurrency(totalPending)}</Badge>}
        </div>
        {loadingList && <div className='text-center'><Spinner size='sm' animation='border' /></div>}
        {!loadingList && list.length === 0 && <div className='text-muted'>ยังไม่มีรายการ</div>}
        {list.map((r) => (
          <div key={r.id} className='d-flex align-items-center gap-2 mb-2 p-2' style={{ background: '#f7f7f7', borderRadius: 6 }}>
            {r.hasImage
              ? (imgCache[r.id]
                  ? <img src={imgCache[r.id]} alt='' onClick={() => setZoom(imgCache[r.id])}
                      style={{ width: 42, height: 42, objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }} />
                  : <div style={{ width: 42, height: 42, borderRadius: 4, background: '#e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spinner size='sm' animation='border' /></div>)
              : <div style={{ width: 42, height: 42, borderRadius: 4, background: '#e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><i className='fa-solid fa-money-bill' /></div>}
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: '#2e7d32' }}>{formatCurrency(r.amount)} กีบ</div>
              <div style={{ fontSize: 10, color: '#888' }}>{formatDate(r.collectedAt)}{r.note ? ` · ${r.note}` : ''}</div>
            </div>
            <Badge bg='secondary' style={{ fontSize: 9 }}>รอลงบัญชี</Badge>
            {
              /* check role admin or debhead */
              (role === 'super admin' || role === 'debhead') && (
                <Button size='sm' variant='outline-danger' style={{ fontSize: 11 }} title='ลบรายการ'
                  onClick={() => setConfirmDel(r)}
                >
                  <i className='fa-solid fa-trash' />
                </Button>
              )
            }
          </div>
        ))}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='outline-secondary' size='sm' onClick={onClose} disabled={saving}>ปิด</Button>
      </Modal.Footer>
      {zoom && (
        <div onClick={() => setZoom(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.85)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img src={zoom} alt='' style={{ maxWidth: '92%', maxHeight: '92%' }} />
        </div>
      )}
      {/* Confirm delete modal */}
      <Modal show={!!confirmDel} onHide={() => !deleting && setConfirmDel(null)} centered size='sm'>
        <Modal.Body className='text-center' style={{ fontSize: 13 }}>
          <i className='fa-solid fa-triangle-exclamation text-danger' style={{ fontSize: 28 }} />
          <div className='mt-2' style={{ fontWeight: 600 }}>ต้องการลบรายการนี้?</div>
          {confirmDel && (
            <div className='text-muted mt-1' style={{ fontSize: 12 }}>
              {formatCurrency(confirmDel.amount)} กีบ · {formatDate(confirmDel.collectedAt)}
            </div>
          )}
          <div className='text-muted mt-1' style={{ fontSize: 11 }}>ลบแล้วกู้คืนไม่ได้</div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='outline-secondary' size='sm' onClick={() => setConfirmDel(null)} disabled={deleting}>ยกเลิก</Button>
          <Button variant='danger' size='sm' onClick={doDelete} disabled={deleting}>
            {deleting ? <Spinner size='sm' animation='border' /> : 'ลบ'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Modal>
  )
}



// ─── EmployeeZoneManager (จัดการผู้ติดตาม) ────────────────────
function EmployeeZoneManager({
  uid, allZones,
}: {
  uid: string
  allZones: string[]
}) {
  const [loading, setLoading] = React.useState(true)
  const [employees, setEmployees] = React.useState<AdminRecord[]>([])
  const [editZones, setEditZones] = React.useState<Record<string, string[]>>({})
  const [saving, setSaving] = React.useState<string>('')
  const [search, setSearch] = React.useState('')
  const [zoneSearch, setZoneSearch] = React.useState('')

  React.useEffect(() => {
    ; (async () => {
      try {
        const [adminRaw, followerRaw] = await Promise.all([
          dbRead<Record<string, AdminRecord>>(DB_ADMIN),
          dbRead<Record<string, FollowerDoc>>(DB_FOLLOWER),
        ])
        const emps = Object.values(adminRaw || {})
          .filter((e) => e.role === 'debfllw' || e.role === 'debadmn' || e.role === 'debhead')
        setEmployees(emps)

        const fMap: Record<string, FollowerDoc> = followerRaw || {}
        // init editZones
        const ez: Record<string, string[]> = {}
        emps.forEach((e) => {
          ez[e.key] = fMap[e.key]?.zones || []
        })
        setEditZones(ez)
      } catch (e: any) {
        toastDanger(e?.message || 'โหลดพนักงานไม่สำเร็จ')
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  const toggleZone = (empKey: string, zone: string) => {
    setEditZones((prev) => {
      const cur = prev[empKey] || []
      return {
        ...prev,
        [empKey]: cur.includes(zone) ? cur.filter((z) => z !== zone) : [...cur, zone],
      }
    })
  }

  const saveEmployee = async (empKey: string) => {
    setSaving(empKey)
    try {
      const zones = editZones[empKey] || []
      await dbUpdate(DB_FOLLOWER_BY_ID(empKey), { uid: empKey, zones, updatedAt: Date.now() })
      toastSuccess('บันทึกโซนเรียบร้อย')
    } catch (e: any) {
      toastDanger(e?.message || 'บันทึกไม่สำเร็จ')
    } finally {
      setSaving('')
    }
  }

  const saveAll = async () => {
    setSaving('all')
    try {
      await Promise.all(
        employees.map((e) => dbUpdate(DB_FOLLOWER_BY_ID(e.key), {
          uid: e.key, zones: editZones[e.key] || [], updatedAt: Date.now(),
        }))
      )
      toastSuccess('บันทึกทั้งหมดเรียบร้อย')
    } catch (e: any) {
      toastDanger(e?.message || 'บันทึกไม่สำเร็จ')
    } finally {
      setSaving('')
    }
  }

  if (loading) return <div className='text-center p-4'><Spinner animation='border' variant='warning' /></div>

  const filtered = employees.filter((e) => {
    const matchSearch = !search ||
      (e.name || '').toLowerCase().includes(search.toLowerCase()) ||
      (e.email || '').toLowerCase().includes(search.toLowerCase()) ||
      e.key.includes(search)
    const matchZone = !zoneSearch ||
      (editZones[e.key] || []).some((z) => z.toLowerCase().includes(zoneSearch.toLowerCase()))
    return matchSearch && matchZone
  })

  return (
    <div>
      <div className='d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2'>
        <div style={{ fontSize: 13, color: '#555' }}>
          จัดสรรว่าใครดูแลโซนไหน (เฉพาะ debfllw/debadmn/debhead) • พนักงาน {employees.length} คน / โซน {allZones.length} โซน
        </div>
        <div className='d-flex gap-2'>
          <Button size='sm' variant='outline-secondary' onClick={saveAll} disabled={saving === 'all'}>
            {saving === 'all' ? <Spinner size='sm' animation='border' /> : <><i className='fa-solid fa-floppy-disk me-1' />บันทึกทั้งหมด</>}
          </Button>
        </div>
      </div>

      <Row className='g-2 mb-3'>
        <Col xs={12} md={5}>
          <Form.Control size='sm' placeholder='ค้นหาพนักงาน ชื่อ / เบอร์ ' value={search} onChange={(e) => setSearch(e.target.value)} />
        </Col>
        <Col xs={8} md={5}>
          <Form.Control size='sm' placeholder='ค้นหาโซน' value={zoneSearch} onChange={(e) => setZoneSearch(e.target.value)} />
        </Col>
        <Col xs={4} md={2}>
          <Button size='sm' variant='outline-danger' className='w-100' onClick={() => { setSearch(''); setZoneSearch('') }}>ล้าง</Button>
        </Col>
      </Row>

      {filtered.length === 0 && <Alert variant='info' style={{ fontSize: 13 }}>ไม่พบพนักงาน</Alert>}

      <Table bordered hover style={{ fontSize: 12 }}>
        <thead className='table-light'>
          <tr>
            <th style={{ width: '25%' }}>พนักงาน</th>
            <th style={{ width: '20%' }}>สรุปโซน</th>
            <th>เลือกโซนที่ดูแล</th>
            <th style={{ width: '80px' }}></th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((emp) => {
            const zones = editZones[emp.key] || []
            return (
              <tr key={emp.key}>
                <td>
                  <div style={{ fontWeight: 600 }}>{emp.name || emp.email}</div>
                  <div style={{ fontSize: 10, color: '#888' }}>{emp.key}</div>
                  <Badge bg='secondary' style={{ fontSize: 9 }}>{emp.role}</Badge>
                </td>
                <td>
                  {zones.length === 0
                    ? <span className='text-muted'>0 โซน</span>
                    : <Badge bg='primary' style={{ fontSize: 10 }}>{zones.length} โซน</Badge>
                  }
                  {zones.length > 0 && (
                    <div style={{ fontSize: 10, color: '#555', marginTop: 2 }}>{zones.join(', ')}</div>
                  )}
                </td>
                <td>
                  <div className='d-flex flex-wrap gap-2'>
                    {allZones.map((z) => {
                      // count customers per zone
                      return (
                        <Form.Check
                          key={z}
                          type='checkbox'
                          id={`zone_${emp.key}_${z}`}
                          label={z}
                          checked={zones.includes(z)}
                          onChange={() => toggleZone(emp.key, z)}
                          inline
                        />
                      )
                    })}
                    {allZones.length === 0 && <span className='text-muted' style={{ fontSize: 11 }}>ไม่มีโซนในระบบ</span>}
                  </div>
                </td>
                <td>
                  <Button
                    size='sm' variant='warning'
                    disabled={saving === emp.key}
                    onClick={() => saveEmployee(emp.key)}
                    style={{ fontSize: 11 }}
                  >
                    {saving === emp.key ? <Spinner size='sm' animation='border' /> : 'บันทึก'}
                  </Button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </Table>
    </div>
  )
}

// ─── Main DebtTracking ────────────────────────────────────────
export default function DebtTracking({ role, previewUid }: { role: string; previewUid?: string }) {
  const { currentUser } = React.useContext(AuthContext)
  // ถ้ามี previewUid ให้ใช้แทน uid จริง (super admin preview mode)
  const myUid = previewUid || currentUser?.uid || ''

  const [loading, setLoading] = React.useState(true)
  const [customers, setCustomers] = React.useState<CustomerReg[]>([])
  const [loans, setLoans] = React.useState<Record<string, LoanDataFollow>>({})
  const [installMap, setInstallMap] = React.useState<Record<string, InstallmentItem[]>>({})
  const [myZones, setMyZones] = React.useState<string[]>([])
  const [following, setFollowing] = React.useState<Record<string, FollowingDoc>>({})

  const [search, setSearch] = React.useState('')
  const [zoneFilter, setZoneFilter] = React.useState('ทุกโซน')
  const [viewMode, setViewMode] = React.useState<'Grid' | 'List'>('Grid')
  const [activeTab, setActiveTab] = React.useState<'list' | 'manage'>('list')

  // detail modal
  const [detailReg, setDetailReg] = React.useState<CustomerReg | null>(null)
  const [showDetail, setShowDetail] = React.useState(false)

  // contact modal (following)
  const [contactReg, setContactReg] = React.useState<CustomerReg | null>(null)
  const [showContact, setShowContact] = React.useState(false)
  const [receiptReg, setReceiptReg] = React.useState<CustomerReg | null>(null)
  const [showReceipt, setShowReceipt] = React.useState(false)

  // custom zone modal
  const [zoneReg, setZoneReg] = React.useState<CustomerReg | null>(null)
  const [showZone, setShowZone] = React.useState(false)

  const isHead = role === 'super admin' || role === 'debhead' || role === 'acchead'

  React.useEffect(() => {
    // reset เมื่อ uid เปลี่ยน (preview mode)
    setMyZones([])
    setCustomers([])
    setLoans({})
    setInstallMap({})
    setFollowing({})
    setLoading(true)
      ; (async () => {
        try {
          // load my zones (if follower)
          if (!isHead) {
            const fd = await dbRead<FollowerDoc>(DB_FOLLOWER_BY_ID(myUid))
            const zones = fd?.zones
            // zones อาจเป็น array หรือ object จาก Firebase
            setMyZones(Array.isArray(zones) ? zones : Object.values(zones || {}))
          }

          const list = await getAllCustomers('active')
          setCustomers(list)

          const fol = await dbRead<Record<string, FollowingDoc>>(DB_FOLLOWING)
          setFollowing(fol || {})

          const lMap: Record<string, LoanDataFollow> = {}
          const iMap: Record<string, InstallmentItem[]> = {}
          await Promise.all(
            list.map(async (c) => {
              const [l, ins] = await Promise.all([
                dbRead<LoanData>(PATH.loan(c.key)),
                dbRead<Record<string, InstallmentItem>>(PATH.payin(c.key)),
              ])
              /* ดึงข้อมูลจาก path DB_FOLLOWING_CUSTOM_ZONE */
              const customZoneData = await dbRead<FollowingDoc>(DB_FOLLOWING_CUSTOM_ZONE_BY_ID(c.key, l?.zone || ''))
              if (l) lMap[c.key] = { ...l, zoneOld: l.zone, zone: customZoneData?.zone || '' }
              if (ins) iMap[c.key] = Object.values(ins).filter(Boolean).sort((a, b) => a.seq - b.seq)
              else iMap[c.key] = []
            })
          )

          setLoans(lMap)
          setInstallMap(iMap)
        } catch (e: any) {
          toastDanger(e?.message || 'โหลดไม่สำเร็จ')
        } finally {
          setLoading(false)
        }
      })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myUid, isHead])

  // ─── Filter ───────────────────────────────────────────────────
  const allZones = React.useMemo(() => {
    const set = new Set<string>()
    customers.forEach((c) => { const z = loans[c.key]?.zone; if (z) set.add(z) })
    return Array.from(set).sort()
  }, [customers, loans])

  // แปลง zone string/array → array เช่น "B+C"→["B","C"], ["B"]→["B"]
  const parseZone = (z: any): string[] => {
    if (!z) return []
    if (Array.isArray(z)) return z
    return String(z).split(/[+,]/).map((s: string) => s.trim()).filter(Boolean)
  }

  const noZoneCount = React.useMemo(
    () => customers.filter((c) => parseZone(loans[c.key]?.zone).length === 0).length,
    [customers, loans]
  )

  const contactCountOf = (id: string) => toContactArray(following[id]?.contacts).length

  const filtered = React.useMemo(() => {
    return customers.filter((c) => {
      const l = loans[c.key]
      const custZones = parseZone(l?.zone)
      // follower: เห็นเฉพาะลูกค้าที่มีโซนตรงกับ myZones อย่างน้อย 1 โซน
      if (!isHead && myZones.length > 0 && l) {
        const hasMatch = custZones.some((z: string) => myZones.includes(z))
        if (!hasMatch) return false
      }
      const matchSearch = !search ||
        c.personal.fullName.toLowerCase().includes(search.toLowerCase()) ||
        c.personal.phone?.includes(search) ||
        /*c.key.includes(search) ||*/
        /*custZones.join('+').includes(search) ||*/
        c.personal.locationLink?.includes(search)
      const matchZone = zoneFilter === 'ทุกโซน'
        || (zoneFilter === '__NOZONE__' ? custZones.length === 0 : custZones.includes(zoneFilter))
      return matchSearch && matchZone
    })
  }, [customers, loans, search, zoneFilter, isHead, myZones])

  if (loading) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{ minHeight: 300 }}>
        <Spinner animation='border' variant='warning' />
      </div>
    )
  }

  return (
    <div className='p-3'>
      {/* Header */}
      <div className='d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2'>
        <div>
          <h5 className='mb-0'>ติดตามหนี้สิน</h5>
          <div className='text-muted' style={{ fontSize: 12 }}>
            {isHead ? 'โฟกัสเบอร์โทรและโลเคชั่น สำหรับติดตามลูกหนี้' : `สายของฉัน: ${myZones.join(', ') || 'ยังไม่ได้กำหนดโซน'}`} uid: {myUid}
          </div>
        </div>
        <div className='d-flex gap-2 flex-wrap'>
          <Badge bg='warning' text='dark'>{filtered.length} รายการ</Badge>
          <Button size='sm' variant={viewMode === 'Grid' ? 'warning' : 'outline-secondary'} onClick={() => setViewMode('Grid')}>Grid</Button>
          <Button size='sm' variant={viewMode === 'List' ? 'warning' : 'outline-secondary'} onClick={() => setViewMode('List')}>List</Button>
        </div>
      </div>

      {/* Tab (head เท่านั้นที่มี manage tab) */}
      {isHead && (
        <div className='d-flex gap-2 mb-3'>
          <Button size='sm' variant={activeTab === 'list' ? 'warning' : 'outline-secondary'} onClick={() => setActiveTab('list')}>
            <i className='fa-solid fa-user-group me-1' />รายละเอียดลูกค้า
          </Button>
          <Button size='sm' variant={activeTab === 'manage' ? 'warning' : 'outline-secondary'} onClick={() => setActiveTab('manage')}>
            <i className='fa-solid fa-id-card me-1' />ติดตามลูกค้า / จัดการพนักงาน
          </Button>
        </div>
      )}

      {/* ─── Tab: Customer list ─────────────────────────────── */}
      {activeTab === 'list' && (
        <>
          <Row className='g-2 mb-3'>
            <Col xs={12} md={6}>
              <Form.Control size='sm' placeholder='ค้นหา ชื่อ / เบอร์โทร / key / โลเคชั่น' value={search} onChange={(e) => setSearch(e.target.value)} />
            </Col>
            <Col xs={8} md={4}>
              <Form.Select size='sm' value={zoneFilter} onChange={(e) => setZoneFilter(e.target.value)}>
                <option value='ทุกโซน'>ทุกโซน</option>
                <option value='__NOZONE__'>⚠️ ยังไม่เซตโซน{noZoneCount ? ` (${noZoneCount})` : ''}</option>
                {allZones.map((z) => <option key={z} value={z}>{z}</option>)}
              </Form.Select>
            </Col>
            <Col xs={2} md={1}>
              <Button size='sm' variant='outline-danger' className='w-100' onClick={() => { setSearch(''); setZoneFilter('ทุกโซน') }}>ล้าง</Button>
            </Col>
            <Col xs={2} md={1}>
              <ZoneSettingModal uid={myUid} role={role} allZones={allZones} />
            </Col>
          </Row>

          {filtered.length === 0 && <Alert variant='info' style={{ fontSize: 13 }}>ไม่พบรายการ</Alert>}

          {viewMode === 'Grid' && (
            <Row className='g-3'>
              {filtered.map((c) => (
                <Col key={c.key} xs={12} md={6} lg={4}>
                  <CustomerTrackCard
                    reg={c}
                    role={role}
                    loan={loans[c.key] || null}
                    installments={installMap[c.key] || []}
                    contactCount={contactCountOf(c.key)}
                    onDetail={() => { setDetailReg(c); setShowDetail(true) }}
                    onContact={() => { setContactReg(c); setShowContact(true) }}
                    onReceipt={() => { setReceiptReg(c); setShowReceipt(true) }}
                    onZone={() => { setZoneReg(c); setShowZone(true) }}
                    onEdit={isHead ? () => { setDetailReg(c); setShowDetail(true) } : undefined}
                  />
                </Col>
              ))}
            </Row>
          )}

          {viewMode === 'List' && (
            <Table size='sm' bordered hover style={{ fontSize: 12 }}>
              <thead className='table-light'>
                <tr>
                  <th>ชื่อ</th><th>เบอร์โทรศัพท์</th><th>โลเคชั่น</th>
                  <th>โซน</th><th>คงเหลือ</th><th>ค้าง</th><th>จ่าย/ทั้งหมด</th><th>สถานะวันนี้</th><th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => {
                  const l = loans[c.key]
                  const ins = installMap[c.key] || []
                  const rem = l ? calcRemain(l, ins) : 0
                  const pd = paidCount(ins)
                  const st = todayStatus(ins)
                  return (
                    <tr key={c.key}>
                      <td>{c.personal.fullName}</td>
                      <td>
                        {c.personal.phone
                          ? <a href={`tel:${c.personal.phone}`} style={{ fontWeight: 700 }}>{c.personal.phone}</a>
                          : '-'}
                      </td>
                      <td style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.personal.locationLink
                          ? <a href={c.personal.locationLink} target='_blank' rel='noreferrer'>{c.personal.locationLink}</a>
                          : '-'}
                      </td>
                      <td>
                        <div className='d-flex align-items-center gap-1'>
                          <span>{l?.zone || '-'}</span>
                          <Button size='sm' variant='outline-secondary' style={{ fontSize: 10, padding: '0 5px' }}
                            title='ปรับโซน' onClick={() => { setZoneReg(c); setShowZone(true) }}>
                            <i className='fa-solid fa-pen' />
                          </Button>
                        </div>
                      </td>
                      <td style={{ color: '#c62828', fontWeight: 700 }}>{l ? formatCurrency(rem) : '-'}</td>
                      <td style={{ color: '#f44336' }}>{ins.length - pd}</td>
                      <td>{pd}/{ins.length}</td>
                      <td>
                        <Badge bg={st === 'paid' ? 'success' : 'danger'} style={{ fontSize: 10 }}>
                          {st === 'paid' ? 'จ่ายแล้ว' : 'ยังไม่จ่าย'}
                        </Badge>
                      </td>
                      <td>
                        <div className='d-flex gap-1'>
                          <Button size='sm' variant='outline-primary' style={{ fontSize: 11 }}
                            onClick={() => { setDetailReg(c); setShowDetail(true) }}>
                            รายละเอียด
                          </Button>
                          <Button size='sm' variant='outline-success' style={{ fontSize: 11 }}
                            onClick={() => { setContactReg(c); setShowContact(true) }}>
                            ติดต่อ{contactCountOf(c.key) ? ` (${contactCountOf(c.key)})` : ''}
                          </Button>
                          <Button size='sm' variant='success' style={{ fontSize: 11 }}
                            onClick={() => { setReceiptReg(c); setShowReceipt(true) }}>
                            <i className='fa-solid fa-camera' /> รับเงิน
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          )}
        </>
      )}

      {/* ─── Tab: Employee zone manager (head only) ─────────── */}
      {activeTab === 'manage' && isHead && (
        <EmployeeZoneManager uid={myUid} allZones={allZones} />
      )}

      {/* Detail modal */}
      <DetailModal
        show={showDetail}
        reg={detailReg}
        loan={detailReg ? (loans[detailReg.key] || null) : null}
        installments={detailReg ? (installMap[detailReg.key] || []) : []}
        onClose={() => { setShowDetail(false); setDetailReg(null) }}
      />

      {/* Contact modal (following) */}
      <ContactModal
        show={showContact}
        reg={contactReg}
        uid={myUid}
        following={contactReg ? (following[contactReg.key] || null) : null}
        onClose={() => { setShowContact(false); setContactReg(null) }}
        onSaved={(id, doc) => setFollowing((prev) => ({ ...prev, [id]: doc }))}
      />
      {/* Receipt modal (field collection) */}
      <ReceiptModal
        show={showReceipt}
        reg={receiptReg}
        uid={myUid}
        role={role}
        onClose={() => { setShowReceipt(false); setReceiptReg(null) }}
      />
      {/* Custom zone modal */}
      <CustomZoneModal
        show={showZone}
        reg={zoneReg}
        loan={zoneReg ? (loans[zoneReg.key] || null) : null}
        uid={myUid}
        allZones={allZones}
        onClose={() => { setShowZone(false); setZoneReg(null) }}
        onSaved={(id, zone) => setLoans((prev) => ({ ...prev, [id]: { ...prev[id], zone } }))}
      />
    </div>
  )
}